import {assign, sendTo, setup, type AnyEventObject } from 'xstate'
import {websocketCallback} from './websockets_machine'
import {
	Player, 
	Team,
	Client_Event_Name,
	Server_Event_Name,
	Server_State_Name,
	Game_Update_Event,
	Subject_Listing, 
	Trivia_Fields,
} from '@quiz-buzz/common'
import {ReactElement} from 'react'
import { all_players_are_in_teams } from './player_machine_state_guards'
import { Notifier, Websocket_Event_Names } from './types'
import { Player_Question } from '@quiz-buzz/common/src/models/Trivia'

export interface ClientContext {
	player_name: string
	is_trial: boolean
	awaiting_server_response: boolean
	game_code: string
	player_secret: string
	host_nickname: string
	players: Player[]
	teams: Team[]
	whos_buzzer_is_active: string | undefined
	teams_enabled: boolean
	trivia_topics?: Trivia_Fields
	chosen_trivia_topic?: Subject_Listing
	round_num: number
	question_num: number
	question: Player_Question
	question_text: string
	modal_header?: string
	modal_content?: ReactElement
	modal_confirm_action?: () => void
	modal_cancel_action?: () => void
}

export interface EventReceiver {
	send: (event: Event) => void
}

export enum Client_State_Name {
	splash = 'splash',
	registering_player = 'registering_player',
	connecting_player = 'connecting_player',
	player_game_setup = 'player_game_setup',
	player_in_game = 'player_in_game',
	player_finished = 'player_finished',
	player_trial_ended = 'player_trial_ended',
	connection_closed = 'connection_closed',
	reconnected = 'reconnected',
	restore_state = 'restore_state',
}

function add_standard_player_message_fields(
	event: AnyEventObject,
	context: ClientContext,
) {
	return {
		type: event.type,
		detail: {
			...event.detail,
			player_name: context.player_name,
			player_secret: context.player_secret,
			game_code: context.game_code,
		},
	}
}

interface State_Machine_Side_Effect_Functions {
	clear_game_link?: () => void,
	toast?: Notifier,
	buzzer_test_callback?: (name: string) => void
}

const helpers: State_Machine_Side_Effect_Functions = {}

export const set_player_machine_helpers = (new_helpers: State_Machine_Side_Effect_Functions) => {
	helpers.clear_game_link = new_helpers.clear_game_link
	helpers.toast = new_helpers.toast
	helpers.buzzer_test_callback = new_helpers.buzzer_test_callback
}

export const player_state_machine = setup({
	types: {
		context: {} as ClientContext,
	},
	actors: {
		websocketCallback,
	},
	actions: {
		log_error: ({event, context}) => {
			const error = {
				error: 'event_error',
				detail: {
					event: event,
					context,
				},
			}
			console.log(error)
		},
		event_error: ({event, self}) => {
			console.log(event)
			if (event.detail?.error_message) {
				helpers.toast?.error(
					event.detail.error_message,
				)
			}
			else {
				helpers.toast?.error(
					`event [${event.type}] from source: [${
						event.detail?.source
					}] not valid in state [${self.getSnapshot().value}]`,
				)
			}
			
		},
		publish_error: ({event}) => {
			helpers.toast?.error(`${event.type}, ${event.detail?.message}`)
		},
		set_trial_mode: assign({
			is_trial: () => true,
		}),
		start_waiting_for_server: assign({
			awaiting_server_response: () => true,
		}),
		stop_waiting_for_server: assign({
			awaiting_server_response: () => false,
		}),
		set_host_nickname: assign({
			host_nickname: ({event}) => event.detail.host_nickname,
		}),
		update_game_state: assign({
			players: ({event}) => (event as Game_Update_Event).detail.players,
			teams: ({event}) => (event as Game_Update_Event).detail.teams,
			whos_buzzer_is_active: ({event}) =>
				(event as Game_Update_Event).detail.whos_buzzer_is_active,
			round_num: ({event}) =>
				(event as Game_Update_Event).detail.current_round,
			question_num: ({event}) =>
				(event as Game_Update_Event).detail.current_question,
			teams_enabled: ({event}) =>
				(event as Game_Update_Event).detail.teams_enabled,
		}),
		update_test_buzzer: ({event}) => {
			helpers.buzzer_test_callback && helpers.buzzer_test_callback(event.detail.player_name)
		},
		update_trivia_topics: assign({
			trivia_topics: ({ event }) => event.detail
		}),
		clear_trivia_topics: assign({
			trivia_topics: () => ({ fields: [] })
		}),
		reset_chosen_trivia_topic: assign({
			chosen_trivia_topic: () => undefined
		}),
		player_registered: assign({
			player_name: ({event}) => event.detail.player_name,
			player_secret: ({event}) => event.detail.player_secret,
			game_code: ({event}) => event.detail.game_code,
		}),
		handle_player_buzzed: assign({
			whos_buzzer_is_active: ({event}) => event.detail.whos_buzzer_is_active,
		}),
		set_game_code: assign({
			game_code: ({ event }) => { console.log('setting player game code:', event.detail.game_code); return event.detail.game_code }
		}),
		reset_context: assign({
			player_name: () => '',
			game_code: () => '',
			awaiting_server_response: () => false,
			player_secret: () => '',
			host_nickname: () => '',
			players: () => [],
			teams: () => [],
			round_num: () => 0,
			question_num: () => 0,
			is_trial: false,
			trivia_topics: undefined,
			chosen_trivia_topic: undefined,
			whos_buzzer_is_active: () => undefined,
			question: {
				question_text: '',
				difficulty: 1
			},
			question_text: () => '',
		}),
		clear_game_code_hash: () => {
			helpers.clear_game_link && helpers.clear_game_link()
		},
		notify_feedback_received: () => {
			helpers.toast?.info('feedback received, thanks!')
		},
		notify_websocket_error: () => {
			helpers.toast?.error('Cant connect, game may have ended')
		},
	},
	guards: {
		has_player_details: ({event}) =>
			!!event.detail.player_name && !!event.detail.game_code,
		all_players_are_in_teams: all_players_are_in_teams,
		no_player_secret: ({context}) =>
			!context.player_secret,
		restore_player_game_setup: ({event, context}) =>
			event.detail?.state === Server_State_Name.pre_game_setup &&
			!!context.player_secret,
		restore_player_in_game: ({event, context}) =>
			event.detail?.state === Server_State_Name.in_play &&
			!!context.player_secret,
		restore_player_finished: ({event, context}) =>
			event.detail?.state === Server_State_Name.finished &&
			!!context.player_secret,
	},
}).createMachine({
	initial: Client_State_Name.splash,
	invoke: {
		id: 'websocketCallback',
		src: 'websocketCallback',
	},
	context: {
		player_name: '',
		game_code: '',
		is_trial: false,
		awaiting_server_response: false,
		player_secret: '',
		host_nickname: '',
		players: [],
		teams: [],
		teams_enabled: false,
		whos_buzzer_is_active: '',
		question_num: 1,
		round_num: 1,
		question: {
			question_text: '',
			difficulty: 1,
		},
		question_text: '',
	},
	on: {
		[Server_Event_Name.game_expired]: {
			actions: ['stop_waiting_for_server', 'reset_context', 'clear_game_code_hash'],
		},
		[Client_Event_Name.send_feedback]: {
			actions: [
				'start_waiting_for_server',
				sendTo('websocketCallback', ({ event, context }) => {
					if (context.player_secret) {
						return add_standard_player_message_fields(event, context)
					}
					return event
				}),
			],
		},
		[Server_Event_Name.feedback_received]: {
			actions: ['stop_waiting_for_server', 'notify_feedback_received']
		},
		'*': {
			actions: ['event_error'],
		},
	},
	states: {
		[Client_State_Name.splash]: {
			entry: ['stop_waiting_for_server', 'reset_context'],
			on: {
				[Client_Event_Name.navigate_back]: {},
				[Client_Event_Name.start_player_registration]: {
					actions: ['set_game_code'],
					target: Client_State_Name.connecting_player,
				},
				[Websocket_Event_Names.websocket_closed]: {
					target: Client_State_Name.splash,
				},
			},
		},
		[Client_State_Name.connecting_player]: {
			entry: [
				'start_waiting_for_server',
				sendTo('websocketCallback', ({ context }) => ({ type: Websocket_Event_Names.connect_websocket, detail: { game_code: context.game_code } })),
			],
			on: {
				[Client_Event_Name.navigate_back]: {},
				[Websocket_Event_Names.websocket_opened]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.registering_player,
				},
				[Websocket_Event_Names.websocket_closed]: {
					actions: ['stop_waiting_for_server', 'clear_game_code_hash', 'reset_context'],
					target: Client_State_Name.connection_closed,
				},
				[Websocket_Event_Names.websocket_error]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.connection_closed,
				},
				[Client_Event_Name.start_player_registration]: {},
			},
		},
		[Client_State_Name.connection_closed]: {
			entry: ['stop_waiting_for_server'],
			on: {
				[Client_Event_Name.navigate_back]: {
					actions: ['reset_context', 'clear_game_code_hash'],
					target: 'splash',
				},
				[Client_Event_Name.reset_game]: {
					target: 'splash',
				},
				[Websocket_Event_Names.connect_websocket]: {
					actions: sendTo('websocketCallback', ({ context }) => ({ type: Websocket_Event_Names.connect_websocket, detail: { game_code: context.game_code } })),
				},
				[Websocket_Event_Names.websocket_opened]: {
					target: Client_State_Name.reconnected,
				},
				[Websocket_Event_Names.websocket_closed]: {
					target: Client_State_Name.connection_closed,
				},
				[Websocket_Event_Names.websocket_error]: {
					actions: ['notify_websocket_error'],
				},
			},
		},
		[Client_State_Name.reconnected]: {
			entry: [
				'start_waiting_for_server',
				sendTo('websocketCallback', ({context}) => {
					if (context.player_secret) {
						return add_standard_player_message_fields(
							{type: Client_Event_Name.player_reconnect},
							context,
						)
					}
				}),
			],
			on: {
				[Client_Event_Name.navigate_back]: {},
				[Server_Event_Name.game_update]: {
					actions: ['stop_waiting_for_server', 'update_game_state'],
					target: Client_State_Name.restore_state,
				},
				[Server_Event_Name.game_doesnt_exist]: {
					actions: ['stop_waiting_for_server', 'reset_context', 'clear_game_code_hash'],
					target: Client_State_Name.splash,
				},
				[Websocket_Event_Names.websocket_closed]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.connection_closed,
				},
			},
		},
		[Client_State_Name.restore_state]: {
			entry: ['stop_waiting_for_server'],
			always: [
				{
					guard: 'restore_player_game_setup',
					target: Client_State_Name.player_game_setup,
				},
				{
					guard: 'restore_player_in_game',
					target: Client_State_Name.player_in_game,
				},
				{
					guard: 'restore_player_finished',
					target: Client_State_Name.player_finished,
				},
				{guard: 'no_player_secret', target: Client_State_Name.splash},
			],
			on: {
				[Websocket_Event_Names.websocket_closed]: {
					target: Client_State_Name.connection_closed,
				},
			},
		},
		[Client_State_Name.registering_player]: {
			on: {
				[Client_Event_Name.navigate_back]: {
					target: Client_State_Name.splash,
				},
				[Client_Event_Name.register_player]: {
					guard: 'has_player_details',
					actions: ['start_waiting_for_server', sendTo('websocketCallback', ({event}) => event)],
				},
				invalid_message: {
					actions: ['stop_waiting_for_server', 'publish_error'],
				},
				[Server_Event_Name.player_name_already_exists]: {
					actions: ['stop_waiting_for_server', 'publish_error'],
				},
				[Server_Event_Name.player_registered]: {
					actions: ['stop_waiting_for_server', 'player_registered'],
					target: Client_State_Name.player_game_setup,
				},
				[Websocket_Event_Names.websocket_closed]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.connection_closed,
				},
				[Websocket_Event_Names.websocket_opened]: {},
				[Server_Event_Name.trial_timeout]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.player_trial_ended,
				},
			},
		},
		[Client_State_Name.player_game_setup]: {
			on: {
				[Client_Event_Name.navigate_back]: {},
				[Client_Event_Name.join_team]: {
					actions: ['start_waiting_for_server', sendTo('websocketCallback', ({event, context}) =>
						add_standard_player_message_fields(event, context),
					)],
				},
				[Server_Event_Name.game_update]: {
					actions: ['stop_waiting_for_server', 'update_game_state'],
				},
				[Client_Event_Name.test_buzz]: {
					actions: ['start_waiting_for_server', sendTo('websocketCallback', ({event, context}) =>
						add_standard_player_message_fields(event, context),
					)],
				},
				[Server_Event_Name.game_started]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.player_in_game,
				},
				[Client_Event_Name.get_player_status]: {
					actions: ['start_waiting_for_server', sendTo('websocketCallback', ({event}) => event)],
				},
				[Server_Event_Name.game_finished]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.player_trial_ended,
				},
				[Server_Event_Name.game_closed]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.player_trial_ended,
				},
				[Websocket_Event_Names.websocket_closed]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.connection_closed,
				},
				[Server_Event_Name.game_doesnt_exist]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.splash,
				},
				[Client_Event_Name.quit]: {
					actions: ['start_waiting_for_server', sendTo('websocketCallback', ({event, context}) =>
						add_standard_player_message_fields(event, context),
					)],
				},
				[Server_Event_Name.player_removed]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.splash,
				},
				[Server_Event_Name.buzzer_test]: {
					actions: ['stop_waiting_for_server', 'update_test_buzzer'],
				},
				[Server_Event_Name.trial_timeout]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.player_trial_ended,
				},
			},
		},
		[Client_State_Name.player_in_game]: {
			on: {
				[Client_Event_Name.navigate_back]: {},
				[Client_Event_Name.buzz]: {
					actions: ['start_waiting_for_server', sendTo('websocketCallback', ({event, context}) =>
						add_standard_player_message_fields(event, context),
					)],
					target: Client_State_Name.player_in_game,
				},
				[Server_Event_Name.player_buzzed]: {
					actions: ['stop_waiting_for_server', 'handle_player_buzzed'],
				},
				[Server_Event_Name.game_update]: {
					actions: ['stop_waiting_for_server', 'update_game_state'],
				},
				[Client_Event_Name.leave_game]: {
					target: Client_State_Name.splash,
				},
				[Client_Event_Name.get_player_status]: {
					actions: ['start_waiting_for_server', sendTo('websocketCallback', ({event}) => event)],
				},
				[Websocket_Event_Names.websocket_closed]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.connection_closed,
				},
				[Server_Event_Name.game_doesnt_exist]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.splash,
				},
				[Server_Event_Name.game_in_setup]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.player_game_setup,
				},
				[Server_Event_Name.game_finished]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.player_finished,
				},
				[Server_Event_Name.trial_timeout]: {
					actions: ['stop_waiting_for_server'],
					target: Client_State_Name.player_trial_ended,
				},
			},
		},
		[Client_State_Name.player_trial_ended]: {
			entry: [
				'stop_waiting_for_server',
				'clear_game_code_hash',
				sendTo('websocketCallback', () => ({
					type: Websocket_Event_Names.client_request_disconnect,
			}))],
			on: {
				[Client_Event_Name.leave_game]: {
					actions: ['reset_context'],
					target: Client_State_Name.splash,
				},
				[Client_Event_Name.navigate_back]: {
				},
				[Websocket_Event_Names.websocket_closed]: {
				},
			},
		},
		[Client_State_Name.player_finished]: {
			entry: ['stop_waiting_for_server'],
			on: {
				[Client_Event_Name.navigate_back]: {},
				[Server_Event_Name.game_closed]: {
					actions: ['reset_context', 'clear_game_code_hash'],
					target: Client_State_Name.splash,
				},
				[Client_Event_Name.quit]: {
					actions: [
						sendTo('websocketCallback', () => ({
							type: Websocket_Event_Names.client_request_disconnect,
						})),
						'reset_context',
						'clear_game_code_hash',
					],
					target: Client_State_Name.splash,
				},
				[Websocket_Event_Names.websocket_closed]: {
					target: Client_State_Name.connection_closed,
				},
				[Server_Event_Name.trial_timeout]: {
					target: Client_State_Name.player_trial_ended,
				},
			},
		},
	},
})


